import {
  FocusEvent,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import styled from '@/styles';

import { useDismiss } from '../hooks/useDismiss';
import { C } from '../Operations.styles';
import {
  clockLabel,
  PICKER_OPTIONS,
  PICKER_ROW_PX,
  pickerIndex,
  pickerScrollTop,
} from '../scheduleForm';

import { ClockIcon } from './icons';

// UI spec §9.1 (field) and §9.2 (the 96-entry quarter-hour list).

const Wrap = styled('div', {
  flex: '1 1 140px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const FieldLabel = styled('span', {
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
});

const Anchor = styled('div', { position: 'relative' });

const Field = styled('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: '36px',
  margin: 0,
  padding: '0 8px',
  border: 'none',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
  color: C.ink,
  font: 'inherit',
  fontSize: '14px',
  textAlign: 'left',
  cursor: 'pointer',
  '& svg': { flexShrink: 0, width: '20px', height: '20px', marginRight: '8px' },
  '&:focus-visible': { outline: `2px solid ${C.darkGreen}` },
  '&:disabled': { cursor: 'not-allowed', opacity: 0.6 },
});

const Text = styled('span', {
  overflow: 'hidden',
  padding: '0 16px 0 8px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  variants: { placeholder: { true: { color: C.grey } } },
});

const List = styled('div', {
  position: 'absolute',
  top: '40px',
  left: 0,
  right: 0,
  height: '148px',
  overflowY: 'auto',
  padding: '4px 0',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.border}, 0 2px 4px 0 rgba(0,0,0,0.08)`,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  variants: {
    // Above the form (20); inside the Repeat modal, above its layers (70).
    layer: { panel: { zIndex: 20 }, modal: { zIndex: 70 } },
  },
  defaultVariants: { layer: 'panel' },
});

const Option = styled('button', {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: `${PICKER_ROW_PX}px`,
  margin: 0,
  padding: '8px 16px',
  border: 'none',
  background: 'none',
  color: C.ink,
  font: 'inherit',
  fontSize: '14px',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover, &:focus-visible': { background: C.hoverGreen, outline: 'none' },
  '&[aria-selected="true"]': {
    background: C.lightGreen,
    color: C.darkGreen,
    fontWeight: 600,
  },
  // An inset ring for keyboard focus, on the selected option too.
  '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${C.darkGreen}` },
});

type Props = {
  /** DOM id stem (label and list ids). */
  id: string;
  label: string;
  /** "HH:MM", or '' while unset. */
  value: string;
  /** "HH:MM" shown in grey while unset; never a value. */
  placeholder: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onPick: (value: string) => void;
  disabled?: boolean;
  layer?: 'panel' | 'modal';
  testIds: { field: string; list: string; optionPrefix: string };
};

const TimePicker = ({
  id,
  label,
  value,
  placeholder,
  open,
  onToggle,
  onClose,
  onPick,
  disabled,
  layer,
  testIds,
}: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Off-grid values ("09:07") select the quarter hour before them.
  const selected = pickerIndex(value);
  const start = Math.max(
    0,
    selected >= 0 ? selected : pickerIndex(placeholder),
  );
  // Roving tabindex: one option is tabbable, the arrows move it.
  const [active, setActive] = useState(start);

  useDismiss(anchorRef, open, (reason) => {
    onClose();
    if (reason === 'escape') fieldRef.current?.focus();
  });

  // On open: scroll the value (or the placeholder) into the second visible
  // row, and focus that option without letting focus() scroll again.
  useLayoutEffect(() => {
    if (!open) return;
    setActive(start);
    if (listRef.current) {
      listRef.current.scrollTop = pickerScrollTop(value, placeholder);
    }
    optionRefs.current[start]?.focus({ preventScroll: true });
    // Only on open: picking closes the list anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const moveTo = (index: number) => {
    const next = Math.min(PICKER_OPTIONS.length - 1, Math.max(0, index));
    setActive(next);
    optionRefs.current[next]?.focus();
  };

  const onListKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, number> = {
      ArrowDown: active + 1,
      ArrowUp: active - 1,
      Home: 0,
      End: PICKER_OPTIONS.length - 1,
    };
    if (!(ev.key in step)) return;
    ev.preventDefault();
    moveTo(step[ev.key]);
  };

  // Tabbing (or any focus move) out of the field and list closes it.
  const onBlur = (ev: FocusEvent<HTMLDivElement>) => {
    const next = ev.relatedTarget as Node | null;
    if (open && next && !anchorRef.current?.contains(next)) onClose();
  };

  const labelId = `${id}Label`;
  const listId = `${id}List`;

  return (
    <Wrap>
      <FieldLabel id={labelId}>{label}</FieldLabel>
      <Anchor ref={anchorRef} onBlur={onBlur}>
        <Field
          ref={fieldRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          // The grey placeholder is not a value: say "not set" instead.
          aria-label={`${label}: ${value ? clockLabel(value) : 'not set'}`}
          disabled={disabled}
          data-testid={testIds.field}
          onClick={(ev) => {
            // Keep the opening click from reaching any window clickout.
            ev.stopPropagation();
            onToggle();
          }}
        >
          <Text placeholder={value === ''}>
            {clockLabel(value || placeholder)}
          </Text>
          <ClockIcon />
        </Field>
        {open && (
          <List
            ref={listRef}
            id={listId}
            role="listbox"
            aria-labelledby={labelId}
            layer={layer}
            data-testid={testIds.list}
            onKeyDown={onListKeyDown}
            // Clicking an option must not move focus first (that would blur
            // the focused option and could close the list mid-click).
            onMouseDown={(ev) => ev.preventDefault()}
          >
            {PICKER_OPTIONS.map((o, i) => (
              <Option
                key={o.value}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                type="button"
                role="option"
                tabIndex={i === active ? 0 : -1}
                aria-selected={i === selected}
                data-testid={`${testIds.optionPrefix}${o.label}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onPick(o.value);
                  fieldRef.current?.focus();
                }}
              >
                {o.label}
              </Option>
            ))}
          </List>
        )}
      </Anchor>
    </Wrap>
  );
};

export default TimePicker;
